import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import queryString from "query-string";
import {
  Users,
  UserPlus,
  Filter,
  Eye,
  Trash2,
  Mail,
  ShieldCheck,
  Edit3,
  MoreVertical,
} from "lucide-react";

// Component Imports
import MainLayout from "../../../layout";
import DeleteModal from "../../../components/modal/delete-modal";
import Loader from "../../../assets/icons/loader/loader";
import { url } from "../../../config";
import AdminFilterDrawer from "../../../components/filter/admin-filter-drawer";
import Pagination from "../../../components/pagination";
import FilterDrawer from "../../../components/filter/filter-drawer";
import { uiTypes } from "../../../utils/types";

const ListUsers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParamsData = new URLSearchParams(location.search);
  const page_no = parseInt(queryParamsData.get("page") ?? 1);

  const [data, setData] = useState({ rows: [], page: 1, count: 0 });
  const [loading, setLoading] = useState(true);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [deleteModalShow, setdeleteModalShow] = useState(false);
  const [selectDelete, setSelectDelete] = useState("");

  const fetchData = () => {
    const rawFilters = queryString.parse(location.search, {
      arrayFormat: "bracket",
    });
    const filters = normalizeParsedParams(rawFilters);

    setLoading(true);
    axios
      .post(`${url}/ffadmin/list`, { page: page_no, ...filters })
      .then((res) => {
        setData(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setLoading(false);
      });
  };

  function normalizeParsedParams(parsed) {
    const result = {};
    for (const [key, value] of Object.entries(parsed)) {
      const match = key.match(/^(\w+)\[(\w+)\]$/);
      if (match) {
        const [, parent, child] = match;
        if (!result[parent]) result[parent] = {};
        result[parent][child] = value;
      } else {
        result[key] = value;
      }
    }
    return result;
  }

  useEffect(() => {
    fetchData();
  }, [page_no, location.search]);

  const deleteFnc = (id) => {
    axios
      .post(`${url}/ffadmin/delete`, { id })
      .then(() => {
        setdeleteModalShow(false);
        fetchData();
      })
      .catch((err) => console.error("Delete Error:", err));
  };

  const columns = [
    {
      name: "name",
      ui: {
        type: uiTypes.TEXT,
        list: true, // Set to true (or omit) to display in the filter list
      },
    },
  ];

  return (
    <MainLayout>
      <DeleteModal
        isOpen={deleteModalShow}
        onClose={() => {
          setdeleteModalShow(false);
          setSelectDelete("");
        }}
        btnFnc={() => deleteFnc(selectDelete)}
      />

      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        columns={columns}
        queryParamsData={queryParamsData}
      />

      <div className="flex flex-col h-full bg-[#F8FAFC] relative overflow-hidden rounded-b-2xl">
        {/* Background Ghost Icon */}

        {/* --- Header --- */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg text-white shadow-lg shadow-blue-200">
              <ShieldCheck size={20} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Admin Users</h1>
              <p className="text-xs text-slate-500 font-medium">
                Manage system access and roles
              </p>
              <Users
                size={80}
                className="absolute right-80 top-0 text-slate-200/30 pointer-events-none z-0"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterDrawerOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-semibold hover:bg-slate-50 transition-all shadow-sm"
            >
              <Filter size={16} /> Filters
            </button>
            <Link
              to="/admin/users/create"
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
            >
              <UserPlus size={18} /> Create User
            </Link>
          </div>
        </header>

        {/* --- Table Section --- */}
        <main className="flex-1 overflow-auto p-2 z-10">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden h-full flex flex-col justify-between">
            <div className="overflow-x-auto custom-scrollbar">
              <table className="w-full text-left border-collapse">
                <thead className="sticky top-0 z-10 bg-slate-50 shadow-sm">
                  <tr className="border-b border-slate-100">
                    <th className="sticky top-0 bg-slate-50 px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                      #
                    </th>
                    <th className="sticky top-0 bg-slate-50 px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                      User Details
                    </th>
                    <th className="sticky top-0 bg-slate-50 px-6 py-4 text-[11px] font-bold text-slate-400 uppercase tracking-widest text-center">
                      Status
                    </th>
                    <th className="px-4 sm:px-6 py-4 text-[10px] sm:text-[11px] font-bold text-slate-400 uppercase tracking-widest text-right sticky right-0 z-50 bg-slate-50 border-b border-slate-100 shadow-[-1px_0_0_0_#f1f5f9]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="py-20 text-center">
                        <div className="flex justify-center">
                          <div className="h-40 w-40">
                            <Loader />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : data.rows?.length > 0 ? (
                    data.rows.map((row, index) => (
                      <tr
                        key={row.id}
                        className="hover:bg-slate-50/80 transition-colors group"
                      >
                        <td className="px-6 py-4 text-sm text-slate-400 font-medium">
                          {(data.page - 1) * 10 + (index + 1)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-blue-600 font-bold text-xs border border-slate-200">
                              {row?.name?.charAt(0).toUpperCase() || (
                                <Users size={14} />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-slate-700 group-hover:text-blue-600 transition-colors">
                                {row?.name || "Unnamed User"}
                              </span>
                              <span className="text-xs text-slate-400 flex items-center gap-1">
                                <Mail size={12} /> {row?.email}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                              row?.enabled
                                ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                : "bg-red-50 text-red-600 border-red-100"
                            }`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${row?.enabled ? "bg-emerald-500" : "bg-red-500"}`}
                            ></span>
                            {row?.enabled ? "Active" : "Disabled"}
                          </span>
                        </td>
                        <td className="px-4 sm:px-6 sm:py-3 text-right sticky right-0 z-[1] bg-white border-t border-slate-50 shadow-[-1px_0_0_0_#f1f5f9] group-hover:bg-[#f9fafb]">
                          <RowActions
                            row={row}
                            tableName={"/admin/users"}
                            setdeleteModalShow={setdeleteModalShow}
                            setSelectDelete={setSelectDelete}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="4"
                        className="py-20 text-center text-slate-400 font-medium"
                      >
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* --- Simple Pagination Placeholder --- */}
            <div className="px-6 py-2 bg-slate-50/30 border-t border-slate-100 flex justify-end items-center">
              <div className="px-2 sm:px-4 bg-white flex justify-center sm:justify-end items-center">
                <div
                  className={`w-full ${loading ? "pointer-events-none opacity-45" : ""}`}
                >
                  <Pagination
                    totalPages={parseInt(data?.count) ?? 0}
                    pagination={parseInt(data?.page) ?? 0}
                    path={`/admin/users/list`}
                    // filters={filters}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </MainLayout>
  );
};

const RowActions = ({
  row,
  tableName,
  setdeleteModalShow,
  setSelectDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="flex justify-end items-center" ref={dropdownRef}>
      {/* --- Desktop View: Show Icons (md and up) --- */}
      <div className="hidden md:flex items-center gap-1">
        <Link
          to={`/admin/users/view/${row["id"]}`}
          className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
          title="View"
        >
          <Eye size={18} />
        </Link>
        <Link
          to={`/admin/users/edit/${row["id"]}`}
          className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
          title="Edit"
        >
          <Edit3 size={18} />
        </Link>
        <button
          onClick={() => {
            setdeleteModalShow(true);
            setSelectDelete(row["id"]);
          }}
          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
          title="Delete"
        >
          <Trash2 size={18} />
        </button>
      </div>

      {/* --- Mobile View: Three Dots Dropdown (Below md) --- */}
      <div className="md:hidden relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`p-2 rounded-lg transition-all ${
            isOpen
              ? "bg-slate-100 text-slate-900"
              : "text-slate-400 hover:bg-slate-50"
          }`}
        >
          <MoreVertical size={20} />
        </button>

        {isOpen && (
          <div className="absolute right-10 top-[-1rem] mt-2 w-44 bg-white border border-slate-200 rounded-xl shadow-xl z-[100] overflow-hidden animate-in fade-in zoom-in duration-100 ">
            <div className="flex  p-1.5">
              <Link
                to={`/admin/users/view/${row["id"]}`}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg"
                onClick={() => setIsOpen(false)}
              >
                <Eye size={16} className="text-primary" />
              </Link>
              <Link
                to={`/admin/users/edit/${row["id"]}`}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 rounded-lg"
                onClick={() => setIsOpen(false)}
              >
                <Edit3 size={16} className="text-amber-600" />
              </Link>
              <div className="my-1 border-t border-slate-100" />
              <button
                onClick={() => {
                  setdeleteModalShow(true);
                  setSelectDelete(row["id"]);
                  setIsOpen(false);
                }}
                className="flex items-center gap-3 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg text-left"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListUsers;
